import { Box } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { GoalDetailOwnFunds } from "web_myplann/Goals/Components/GoalDetailOwnFunds";
import {
	GoalDetailSolution,
	type PinStep,
} from "web_myplann/Goals/Components/GoalDetailSolution";
import { useGoalPortfolioActions } from "web_myplann/Goals/Hooks/useGoalPortfolioActions";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";

export type WelcomePinTrigger = { hasOwnFunds: boolean };

type Props = {
	goal: GoalDetail;
	goalDto: GoalDetailResponseDTO | undefined;
	welcomePinTrigger: WelcomePinTrigger | null;
	onWelcomePinTriggerHandled: () => void;
};

export const GoalPinningSection: FC<Props> = ({
	goal,
	goalDto,
	welcomePinTrigger,
	onWelcomePinTriggerHandled,
}) => {
	const [pinStep, setPinStep] = useState<PinStep>("closed");
	const [pinPortfolioType, setPinPortfolioType] =
		useState<GoalPortfolioTypeEnum>(GoalPortfolioTypeEnum.Allocated);

	const closePin = () => setPinStep("closed");

	const { pin, unpin, editAllocation, unpinningId } = useGoalPortfolioActions({
		goalId: Number(goal.id),
		goalDto,
		onPinSettled: closePin,
	});

	useEffect(() => {
		if (!welcomePinTrigger) return;
		const { hasOwnFunds } = welcomePinTrigger;
		setPinPortfolioType(
			hasOwnFunds
				? GoalPortfolioTypeEnum.SetAside
				: GoalPortfolioTypeEnum.Allocated,
		);
		setPinStep(hasOwnFunds ? "choose" : "existing");
		onWelcomePinTriggerHandled();
	}, [welcomePinTrigger, onWelcomePinTriggerHandled]);

	const handleSolutionPinStep = (next: PinStep) => {
		if (next === "choose") {
			setPinPortfolioType(GoalPortfolioTypeEnum.Allocated);
		}
		setPinStep(next);
	};

	const handleOwnFundsPin = () => {
		setPinPortfolioType(GoalPortfolioTypeEnum.SetAside);
		setPinStep("choose");
	};

	const handlePinSelect = (picked: GoalPortfolioListItemResponseDTO) => {
		pin(picked, pinPortfolioType);
	};

	const { allocated, setAside } = useMemo(() => {
		const portfolios = goalDto?.portfolios ?? [];
		return {
			allocated: portfolios.filter(
				(p) => p.type === GoalPortfolioTypeEnum.Allocated,
			),
			setAside: portfolios.filter(
				(p) => p.type === GoalPortfolioTypeEnum.SetAside,
			),
		};
	}, [goalDto?.portfolios]);

	const hasOwnFunds = (goalDto?.ownFunds ?? 0) > 0;

	return (
		<>
			{hasOwnFunds && (
				<Box mt={1}>
					<GoalDetailOwnFunds
						goal={goal}
						portfolios={setAside}
						onAddPin={handleOwnFundsPin}
						onUnpin={unpin}
						unpinningId={unpinningId}
					/>
				</Box>
			)}
			<Box mt={1}>
				<GoalDetailSolution
					goal={goal}
					goalDto={goalDto}
					allocatedPortfolios={allocated}
					pinStep={pinStep}
					onPinStepChange={handleSolutionPinStep}
					onPinSelect={handlePinSelect}
					onUnpin={unpin}
					onEditAllocation={editAllocation}
					unpinningId={unpinningId}
				/>
			</Box>
		</>
	);
};
